import { ConsoleDebug } from "@/Common/Api/ConsoleDebug";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { DateTime } from "@/Common/Helpers/DateTime";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { PinFrequency } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseAction, IRequestExecutionContext, IResponseExecutionContext } from "@/Common/Stores/Actions/BaseAction";
import { ServerEventWithThen } from "@/Common/Stores/Actions/ServerEvent";
import { ForbiddenStatusCodeErrorHandler } from "./ErrorHandlers/ForbiddenStatusCodeErrorHandler";
import { ClientResponse } from "./ClientResponse";

/**
 * Manages a list of recent operation codes and routes sent to the backend.
 */
class OperationLogs {
    /**
     * The recent operation codes and routes.
     */
    private operations: string[] = [];

    /**
     * The maximum number of entries to keep in the list.
     */
    private limit: number;

    /**
     * Construct an instance.
     * @param limit The maximum number of entries to keep in the list.
     */
    constructor(limit: number) {
        this.limit = limit;
    }

    /**
     * Add an entry to the list, removing the oldest one if needed.
     * @param operation The operation code or route path to add.
     */
    public enqueue(operation: string): void {
        if (this.operations.length === this.limit)
            this.dequeue();
        this.operations.push(operation);
    }

    /**
     * Remove the oldest entry from the list.
     */
    private dequeue(): string {
        return this.operations.shift();
    }

    /**
     * Get a list of recent operations and route paths, ordered by oldest to newest.
     */
    public getItems(): string[] {
        return this.operations;
    }
}

/**
 * The common interface for request messages to the backend API routed by registered operation.
 */
interface MyleRequest {
    /**
     * The registered operation code. This is auto-populated by the client.
     */
    operation: number;

    /**
     * The message ID used to correlate responses with originating requests. This is auto-populated by the client.
     */
    messageId: string;

    /**
     * The data fields to include in the request.
     */
    [fieldName: string]: any;
}

/**
 * The common interface for response messages from the backend API routed by registered operation.
 */
interface MyleResponse {
    /**
     * The registered operation code.
     */
    Operation: number;

    /**
     * The message ID used to correlate responses with originating requests.
     */
    MessageId: string;

    /**
     * The error messages which occurred while processing the request.
     */
    Errors: ErrorMessage[];

    /**
     * The warning messages which occurred while processing the request.
     */
    Warnings: ErrorMessage[];

    /**
     * The data fields included in the response.
     */
    [fieldName: string]: any;
}

/** Generic headers for Myle API calls. */
const baseMyleApiHeaders = {
    /** The header for the frontend version. */
    "myle-version-frontend": process.env.REACT_APP_VERSION
};

class Client {
    private static staticloadingTimoutReference = [];

    /**
     * A list of recent operations and route paths, ordered by oldest to newest.
     */
    public static operationLogs: OperationLogs = new OperationLogs(6);

    /**
     * Get a list of recent operations and route paths, ordered by oldest to newest.
     */
    public static getOperationLogs(): string[] {
        return this.operationLogs.getItems();
    }

    public static stopLoadingIndicator(): void {
        const timeout = Client.staticloadingTimoutReference.reverse().pop();
        if (timeout)
            clearTimeout(timeout);

        if (Client.staticloadingTimoutReference.length == 0 && sessionStore?.loading) {
            sessionStore.loading = false;
            sessionStore.EmitChangeToListeners();
        }
    }

    public static startLoadingIndicator(): void {
        const timeoutId = window.setTimeout(
            () => {
                sessionStore.loading = true;
                sessionStore.EmitChangeToListeners();
            },
            100
        );

        Client.staticloadingTimoutReference.push(timeoutId);
    }

    private static RawPost<T>(path: string, operation: number, data: Partial<MyleRequest>, onSuccess: (response: T, err: ErrorMessage[], status: string, xhr: JQueryXHR) => void, onServerHandlingError: (response: T, errors: ErrorMessage[]) => void, onClientHandlingError: (response: T, ex: any | JQueryXHR) => void, messageId?: string): void {
        Client.beforeRequest(operation);

        // assuming app runs not as an application of another website
        jQuery.ajax(
            path,
            {
                xhrFields: { withCredentials: true },
                method: "POST",
                headers: baseMyleApiHeaders,
                data: JSON.stringify(Client.prepareMylePayload(operation, messageId, data)),
                processData: false,
                crossDomain: true,
                jsonp: false,
                contentType: "application/json",
                dataType: "json",
                success: (data: MyleResponse, status, xhr) => {
                    Client.afterResponse(xhr);

                    const model = data as any as T;

                    try {
                        // handle server error
                        const hasErrors = data.Errors?.length > 0;

                        const continueHandling = globalServerResponseHandler.handleErrors(Client.getServerEventFromRawData(data));
                        if (!continueHandling)
                            return;

                        if (hasErrors && onServerHandlingError != null)
                            onServerHandlingError(model, data.Errors);
                        else
                            onSuccess(model, data.Errors, status, xhr);
                    }
                    catch (ex) {
                        console.error("Error:", ServerOperations.Server[operation], ex);
                        onClientHandlingError?.(model, ex);
                    }
                },
                error: xhr => {
                    Client.afterResponse(xhr);
                    console.error("Error:", ServerOperations.Server[operation], xhr);
                    onClientHandlingError?.(null, xhr);
                },
                beforeSend: request => Client.beforeSend(request)
            }
        );
    }

    /**
     * Post a request to the MYLE API and get the response.
     * @template TResponse The data model type for the response body, or `void` if the response has no body.
     * @param operation The registered operation for the API endpoint to post.
     * @param data The request body to send.
     */
    public static postAsync<TResponse = void>(operation: number, data: object): Promise<ClientResponse<TResponse>> {
        return new Promise(resolve => {
            try {
                Client.RawPost<TResponse>(
                    apiSettings.buildApiPath(),
                    operation,
                    data,
                    response => resolve(new ClientResponse<TResponse>(response, [])), // on success
                    (response, errors) => resolve(new ClientResponse<TResponse>(response, errors)), // on server handling error
                    (response, ex) => resolve(new ClientResponse<TResponse>(response, ex)) // on client error
                );
            }
            catch (error) {
                resolve(new ClientResponse<TResponse>(null, error)); // error contacting the API (e.g. server not responding)
            }
        });
    }

    /**
     * A beacon request (no response expected) using fetch to MYLE API.
     * This can be used within the page unloading to send a final payload.
     * @param operation The registered operation for the API endpoint to post.
     * @param data The request body to send.
     */
    public static sendBeacon(operation: number, data: object): void {
        const url = apiSettings.buildApiPath();
        const payload = JSON.stringify(Client.prepareMylePayload(operation, undefined, data));
        // Use fetch as we need to include additional request information not possible with navigator.sendBeacon. Setting keepalive allows it to survive the page closing.
        fetch(url, {
            mode: "cors",
            credentials: "include",
            keepalive: true,
            method: "POST",
            headers: {
                "content-type": "application/json",
                "api-key": apiSettings.ApiKey,
                "sessionId": apiSettings.SessionId
            },
            body: payload
        });
    }

    /**
     * Post a request to the XCOM API and get the response.
     * @param remoteOperation The registered operation for the API endpoint to post.
     * @param data The request body to send.
     */
    public static postAsyncXcom<TResponse>(remoteOperation: number, data: Partial<MyleRequest>, clientTag: string, claimId: string | null): Promise<ClientResponse<TResponse>> {
        const localOperation = ServerOperations.Server.Myle_ExternalCommunication_Request;

        return new Promise(resolve => {
            try {
                Client.beforeRequest(localOperation);

                jQuery.ajax(
                    // route for ProxyExternalCommunicationRequest
                    apiSettings.buildAppUrl(`/api/xcom/${clientTag}/${claimId}/api/registeredOperation`),
                    {
                        xhrFields: { withCredentials: true },
                        method: "POST",
                        headers: baseMyleApiHeaders,
                        data: JSON.stringify(
                            Client.prepareMylePayload(
                                localOperation,
                                GuidHelper.NewGuid(),
                                {
                                    RemotePostData: JSON.stringify(Client.prepareMylePayload(remoteOperation, GuidHelper.NewGuid(), data))
                                }
                            )
                        ),
                        processData: false,
                        crossDomain: true,
                        jsonp: false,
                        contentType: "application/json",
                        dataType: "json",
                        success: (data: MyleResponse, status, xhr) => {
                            Client.afterResponse(xhr);

                            const model = data as any as TResponse;

                            try {
                                // handle server error
                                const hasErrors = data.Errors?.length > 0;

                                const continueHandling = globalServerResponseHandler.handleErrors(Client.getServerEventFromRawData(data));
                                if (!continueHandling)
                                    return;

                                if (hasErrors)
                                    resolve(new ClientResponse<TResponse>(model, data.Errors)); // on server handling error
                                else
                                    resolve(new ClientResponse<TResponse>(model, [])); // on success
                            }
                            catch (ex) {
                                console.error("Error:", ServerOperations.Server[localOperation], ex);
                                resolve(new ClientResponse<TResponse>(model, ex)); // on client error
                            }
                        },
                        error: xhr => {
                            Client.afterResponse(xhr);
                            console.error("Error:", ServerOperations.Server[localOperation], xhr);
                            resolve(new ClientResponse<TResponse>(null, xhr as any)); // on client error
                        },
                        beforeSend: request => Client.beforeSend(request)
                    }
                );
            }
            catch (error) {
                resolve(new ClientResponse<TResponse>(null, error)); // error contacting the API (e.g. server not responding)
            }
        });
    }

    /**
     * Post files to the MYLE API using a `multipart/form-data` request.
     * @param route The API endpoint route to submit files to, excluding the base URL.
     * @param files The files to post.
     * @param data The optional request body to post with the files.
     */
    public static postFiles<TResponse>(route: string, files: File[], data: Partial<MyleRequest> = null): Promise<ClientResponse<TResponse>> {
        Client.beforeRequest(route);

        return new Promise(resolve => {
            // create request body
            data = Client.prepareMylePayload(0, GuidHelper.NewGuid(), data);

            const formData = new FormData();
            for (let key in (data || {}))
                formData.append(key, IsNullOrUndefined(data[key]) ? data[key] : JSON.stringify(data[key]));
            files.forEach((file, i) =>
                formData.append("file" + i, file)
            );

            // send request
            const url = apiSettings.buildAppUrl(route);
            jQuery.ajax(
                url,
                {
                    headers: {
                        ...baseMyleApiHeaders,
                        Accept: "application/json"
                    },
                    xhrFields: { withCredentials: true },
                    method: "POST",
                    data: formData,
                    contentType: false, // don't try to convert FormData
                    processData: false,
                    crossDomain: true,
                    success: (data: MyleResponse, status, xhr) => {
                        Client.afterResponse(xhr);

                        const model = data as any as TResponse;

                        try {
                            const continueHandling = globalServerResponseHandler.handleErrors(Client.getServerEventFromRawData(data));
                            if (!continueHandling)
                                return;

                            resolve(new ClientResponse<TResponse>(model, data.Errors));
                        }
                        catch (error) {
                            console.error("Error:", route, { error });
                            resolve(new ClientResponse<TResponse>(model, error));
                        }
                    },
                    error: (xhr, status, error) => {
                        Client.afterResponse(xhr);

                        console.error("Error:", route, { xhr, status, error });
                        resolve(new ClientResponse<TResponse>(null, error));
                    },
                    beforeSend: request => Client.beforeSend(request)
                }
            );
        });
    }

    /**
     * @deprecated Use postAsync instead.
     */
    public static Postv2(operation: number, data: Partial<MyleRequest>, onSuccess: (response: any) => void, onServerHandlingError?: (errors: ErrorMessage[]) => void): void {
        Client.RawPost<any>(apiSettings.buildApiPath(), operation, data, onSuccess, (_, errors) => onServerHandlingError(errors), null);
    }

    /**
     * @deprecated Use postAsync instead.
     */
    public static Post(operation: number, data: Partial<MyleRequest>, onSuccess: (response: any, err: ErrorMessage[], status: string, xhr: JQueryXHR) => void, onClientHandlingError?: (ex: any) => void): void {
        Client.RawPost<any>(apiSettings.buildApiPath(), operation, data, onSuccess, null, (_, ex) => onClientHandlingError(ex));
    }

    public static PostDSQ(operation: number, data: Partial<MyleRequest>, onSuccess: (response: any, err: ErrorMessage[], status: string, xhr: JQueryXHR) => void, onClientHandlingError?: (ex: any) => void): void {
        function wrappedOnSuccess(data: any, err: ErrorMessage[], status: string, xhr: JQueryXHR): void {
            const errors: ErrorMessage[] = data.Errors;
            if (errors.any()) {
                const hasResetNipErrors = errors.some(err => err?.Code === "ClearNIPCache");
                const hasNoKeyErrors = errors.some(err => err?.Code === "KeyNotConnected");

                if (hasResetNipErrors)
                    sessionStore.DSQPin = "";

                if (hasNoKeyErrors) {
                    sessionStore.showPopup({
                        content: "La clé DSQ ne semble pas branchée.",
                        showCancel: true,

                        okLabel: "Désactiver le DSQ et continuer",
                        okAction: () => sessionStore.DSQPrescriptionDisabled = true
                    });
                }
                else
                    sessionStore.showPopup(errors);
            }
            else {
                // Return with no error, reactivate DSQ
                sessionStore.DSQPrescriptionDisabled = false;
                sessionStore.DSQPrescriptionError = 0;
            }

            onSuccess(data, errors, status, xhr);
        }

        function action(): void {
            data.PIN = sessionStore.DSQPin;
            Client.Post(operation, data, wrappedOnSuccess, onClientHandlingError);
        }

        if (sessionStore.hasDsqNIP())
            action();
        else
            sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.DisplayDSQNip, { action });
    }

    /**
     * Open a new window which submits a POST request to a URL.
     * @param url The URL of the window to open.
     * @param data The POST request fields to send.
     * @param windowOptions The raw window options to set (like `toolbar=no,menubar=no`), if applicable.
     * @returns Returns whether the window was opened successfully.
     */
    public static openWindowWithPostData(url: string, data: Record<string, string>, windowOptions?: string): boolean {
        // get unique target to link form & window
        const formTarget = "TempPostForm" + sessionStore.newId();

        // add form to submit POST data
        const form = document.createElement("form");
        form.method = "POST";
        form.action = url;
        form.target = formTarget;
        form.style.display = "none";
        document.body.appendChild(form);

        // add fields for the values to submit
        for (const key of Object.keys(data)) {
            const input = document.createElement("input");
            input.type = "text";
            input.name = key;
            input.value = data[key];
            form.appendChild(input);
        }

        // open window & submit form
        const opened = window.open("", formTarget, windowOptions);
        if (opened)
            form.submit();
        else
            console.error(`Failed opening window with URL '${url}'. The browser may be blocking popups.`); // log for troubleshooting, but the browser should show a separate 'popup blocked' UI message

        // remove temporary form
        form.remove();

        return !!opened;
    }

    public static openRadpdfForm(patientFormId: string, employeeId: string, encounterId: string, encounterCreationDate: Date, editMode: boolean, lastEncounterCreationDate: Date, then: (getTokenResponseData: any) => void = null): void {
        const request = {
            PatientFormId: patientFormId,
            EmployeeId: employeeId,
            EncounterId: encounterId,
            ConsultationDate: encounterCreationDate,
            EditMode: editMode,
            LastEncounterCreationDate: lastEncounterCreationDate
        };

        Client.Post(
            ServerOperations.Server.Myle_Patient_MedicalNote_GenerateToken,
            request,
            response => {
                const radDocumentId: string = response.RadDocumentId;
                const token: string = response.Token;
                const baseUrl = response.EditMode ? cacheStore.RADPdfFormEditUrl : cacheStore.RADPdfFormViewUrl;

                window.open(`${baseUrl}${radDocumentId}?${token}`, "_blank");

                then?.(response);
            }
        );
    }

    /**
     * Send an asynchronous call to the server and parse the response.
     * @param path The API endpoint to call.
     * @param serverOperation The server operation code to invoke.
     * @param subOperation The frontend sub-operation ID. This is never sent to the server, it's only used for response handling.
     * @param requestId A unique identifier for the request, used to link it with the response.
     * @param requestData The data to send in the request.
     * @param context The action sending the request.
     * @param hideDefaultLoading Whether to hide the default loading indicator. Default false.
     * @param onBeforeRequest An optional callback to invoke before sending the request. If this returns false, the request is aborted.
     * @param onBeforeResponse An optional callback to invoke when the response is received. If this is provided, {@link ServerResponseDispatcher.dispatch} is not called.
     * @param onSuccess An optional callback to invoke after the response is received and processed. This is called even if the response contains an error message.
     * @param onError An optional callback to invoke if the request failed (e.g. the server is unavailable). This is not called if the request succeeded but returns an error message.
     * @param onComplete An optional callback to invoke after the response failed, whether it succeeded or failed.
     */
    // eslint-disable-next-line @typescript-eslint/ban-types -- using `Function` as the base for a generic type constraint is fine since the signature is specified
    public Execute<TSuccessCallback extends Function = () => void>(path: string, serverOperation: number, subOperation: number, requestId: string, requestData: Partial<MyleRequest>, context: BaseAction, hideDefaultLoading?: boolean, onBeforeRequest?: IRequestExecutionContext, onBeforeResponse?: IResponseExecutionContext, onSuccess?: TSuccessCallback, onError?: () => void, onComplete?: () => void): void {
        Client.beforeRequest(serverOperation);

        const dto = {};
        if (onBeforeRequest && !onBeforeRequest(serverOperation, subOperation, requestId, requestData, dto))
            return;

        // assuming app runs not as an application of another website
        jQuery.ajax(
            path,
            {
                xhrFields: { withCredentials: true },
                method: "POST",
                headers: baseMyleApiHeaders,
                data: JSON.stringify(Client.prepareMylePayload(serverOperation, requestId, requestData)),
                processData: false,
                crossDomain: true,
                jsonp: false,
                contentType: "application/json",
                dataType: "json",
                beforeSend: request => Client.beforeSend(request),
                success: (data: MyleResponse, status, xhr) => {
                    Client.afterResponse(xhr);

                    const response = Client.getServerEventFromRawData(data, subOperation, requestData, onSuccess);

                    if (!globalServerResponseHandler.handleErrors(response))
                        return;

                    if (!onBeforeResponse || onBeforeResponse(response, data.MessageId, dto))
                        ServerResponseDispatcher.dispatch(response);
                },
                error: xhr => {
                    Client.afterResponse(xhr);

                    const preventDefaultHandling = ForbiddenStatusCodeErrorHandler.handleError(xhr.status, xhr.responseJSON["ErrorMessages"]);
                    if (preventDefaultHandling) {
                        const response = Client.getServerEventFromError(requestId, requestData, serverOperation, xhr.responseJSON["ErrorMessages"], xhr.status, subOperation);
                        ServerResponseDispatcher.dispatch(response);
                    }
                    else {
                        console.error("ajax on error", xhr);
                        context.RemoveRequest(requestId, serverOperation);
                        onError?.();
                    }
                },
                complete: () => {
                    onComplete?.();
                }
            }
        );
    }

    public Post(path: string, operation: number, data: Partial<MyleRequest>, onSuccess: (response: any, error: ErrorMessage[], status: string, xhr: JQueryXHR) => void, onClientHandlingError: (ex: any) => void, messageId?: string): void {
        Client.RawPost(path, operation, data, onSuccess, null, (_, ex) => onClientHandlingError(ex), messageId);
    }

    public PostDSQ(operation: number, data: Partial<MyleRequest>, onSuccess: (response: any, error: ErrorMessage[], status: string, xhr: JQueryXHR) => void, onClientHandlingError?: (ex: any) => void): void {
        Client.PostDSQ(operation, data, onSuccess, onClientHandlingError);
    }

    private static setNewApiKey(request: JQueryXHR): void {
        const apiKey: string = request.getResponseHeader("sth");
        if (apiKey)
            apiSettings.setSession(apiKey, apiSettings.SessionId, "renewal");
    }

    // eslint-disable-next-line @typescript-eslint/ban-types -- using `Function` as the base for a generic type constraint is fine since the signature is specified
    private static getServerEventFromRawData<TSuccessCallback extends Function = () => void>(data: MyleResponse, subOp: number = null, requestDto: Partial<MyleRequest> = null, then: TSuccessCallback = null): ServerEventWithThen<Partial<MyleRequest>, TSuccessCallback> {
        const response = new ServerEventWithThen<Partial<MyleRequest>, TSuccessCallback>(data.Operation, subOp, data.MessageId);
        response.Errors = data.Errors;
        response.Warnings = data.Warnings;
        response.Data = data;
        response.then = then;
        response.requestDto = requestDto;

        return response;
    }

    private static getServerEventFromError(messageId: string, requestDto: any, serverOp: number, errorMessage: string, status: number, subOp: number = null): ServerEventWithThen<Partial<MyleRequest>, () => void> {
        const response = new ServerEventWithThen<Partial<MyleRequest>, () => void>(serverOp, subOp, messageId);
        response.Errors = [new ErrorMessage({ Message: errorMessage })];
        response.Data = {
            Status: status
        };
        response.requestDto = requestDto;

        return response;
    }

    private static prepareMylePayload(operation: number, messageId: string, data: Partial<MyleRequest> | null): MyleRequest {
        if (data == null)
            data = {};

        if (operation)
            data.operation = operation;
        data.messageId = messageId;

        return data as MyleRequest;
    }

    public static getJsonFileContent(filePath: string, onReceiveContent: (data: any) => void): JQueryXHR {
        const resultPromise = jQuery.getJSON(apiSettings.buildAppUrl(filePath));

        resultPromise
            .done((content: any) => {
                onReceiveContent?.(content);
            })
            .fail((jqxhr: JQueryXHR, textStatus) => {
                console.log("Error while loading resource:", filePath, jqxhr.responseText, textStatus);

                onReceiveContent?.(null);
            });

        return resultPromise;
    }

    /**
     * Perform generic client logic before processing a request.
     * @param operation The request server operation or API route.
     */
    private static beforeRequest(operation: number | string): void {
        // refresh counters every 2 minutes
        if (sessionStore != null) { // exclude on pages like HandWrittenNote
            clearTimeout(sessionStore.timeoutReferenceRefreshCounters);
            sessionStore.timeoutReferenceRefreshCounters = window.setTimeout(() => {
                if (sessionStore.refreshCounterLastQuery == null || sessionStore.refreshCounterLastQuery < DateTime.nowDeprecated().addMinutes(-2)) {
                    sessionStore.refreshCounterLastQuery = DateTime.nowDeprecated();

                    sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.RefreshCounters);
                    console.log("Refresh Counters");
                }
            }, 5000); // debounce: after 5 seconds on inactivity
        }

        // debug logging
        const operationName = EnumHelper.getName(ServerOperations.Server, operation);
        this.operationLogs.enqueue(operationName);
        if (ConsoleDebug.displayOps)
            console.log(operationName, operation);
    }

    /**
     * Perform generic client logic before a request is sent.
     * @param request The request being sent.
     */
    private static beforeSend(request: JQueryXHR): void {
        // add headers
        request.setRequestHeader("api-key", apiSettings.ApiKey);
        request.setRequestHeader("sessionId", apiSettings.SessionId);
        if (sessionStore.isMsaActive)
            request.setRequestHeader("msaid", sessionStore.msaId);

        // start loading timer
        Client.startLoadingIndicator();
    }

    /**
     * Perform generic client logic when a response is received (whether it succeeded or failed).
     */
    private static afterResponse(xhr: JQueryXHR): void {
        Client.stopLoadingIndicator();

        if (xhr) {
            this.setNewApiKey(xhr);
            const authRequiredHeader = xhr.getResponseHeader("auth-required");
            if (authRequiredHeader === "true") {
                Client.clearPinTokensBeforeLogout();
                sessionStore.dispatch(LocalOperations.Login.RedirectToReloginPage);
            }
        }
    }

    /**
     * Clear the cached per-session signature PIN frequency tokens for the current employee.
     */
    private static clearPinTokensBeforeLogout(): void {
        if (!sessionStore.getCurrentEmployee()?.Model?.LoginPinFrequency)
            return;

        sessionStore.updateCurrentEmployee(employee => {
            const frequency = employee.LoginPinFrequency;

            employee.LoginPinFrequency = {
                ...frequency,
                ConsultationRequestToken: Client.getTokenIfStillValid(frequency.ConsultationRequestFrequency, frequency.ConsultationRequestToken),
                DocumentToken: Client.getTokenIfStillValid(frequency.DocumentFrequency, frequency.DocumentToken),
                ExamRequestToken: Client.getTokenIfStillValid(frequency.ExamRequestFrequency, frequency.ExamRequestToken),
                FormToken: Client.getTokenIfStillValid(frequency.FormFrequency, frequency.FormToken),
                PrescriptionToken: Client.getTokenIfStillValid(frequency.PrescriptionFrequency, frequency.PrescriptionToken),
                ObstetricalFileToken: Client.getTokenIfStillValid(frequency.ObstetricalFileFrequency, frequency.ObstetricalFileToken)
            };
        });
    }

    /**
     * Get a PIN frequency token if it's not per-session.
     * @param pinFrequency The PIN frequency type.
     * @param token The PIN frequency token.
     */
    private static getTokenIfStillValid(pinFrequency: PinFrequency, token: string): string {
        return pinFrequency !== PinFrequency.EverySession
            ? token
            : null;
    }
}

export { Client };
